/**
 * **The keyboard map. The single source of truth** (`110-design-system.md` §7).
 *
 * > "Every shortcut is listed in the cheatsheet, generated from a single source-of-truth map so
 * > the sheet cannot drift from the bindings."
 *
 * And roadmap M7, criterion 3, which is the harsher way of saying the same thing:
 *
 * > "Pressing `?` shows the shortcut overlay, and **every shortcut listed on it actually
 * > works**. Check them one by one. A stale help overlay is worse than none."
 *
 * So the rule for this file is simple and it is absolute: **a row here is a promise.** Nothing
 * goes in this table that has not been triggered by hand in the running app. If a binding is
 * removed, its row goes with it in the same commit. A row whose `where` is not "Global" states
 * where it works, because "works everywhere" and "works when the tree has focus" are different
 * promises and the sheet must not blur them.
 *
 * This module is data only — it renders the sheet. The bindings themselves live with the
 * components that own them (the palette owns `Mod+K`, Tiptap owns `Mod+B`), because a keymap
 * that also *dispatches* would have to reach into every feature to do it.
 */

export interface Shortcut {
  /** Rendered with `Mod` → ⌘ on macOS, Ctrl elsewhere. `then` renders a leader sequence. */
  keys: string;
  action: string;
  /** Only when it needs saying — a note, not a second description. */
  note?: string;
}

export interface ShortcutSection {
  title: string;
  /** Where these apply. Absent for the global set. */
  where?: string;
  shortcuts: Shortcut[];
}

export const KEYMAP: readonly ShortcutSection[] = [
  {
    title: 'Global',
    shortcuts: [
      { keys: 'Mod+K', action: 'Command palette', note: 'Pages, events, commands' },
      { keys: 'Mod+P', action: 'Palette in page-jump mode' },
      { keys: 'Mod+Shift+F', action: 'Search' },
      { keys: 'Mod+N', action: 'New page (sibling of the current one)' },
      { keys: 'Mod+Shift+N', action: 'New child page' },
      { keys: 'Mod+S', action: 'Save now', note: 'The app autosaves; this flushes the debounce' },
      { keys: 'Mod+E', action: 'Toggle the AI panel' },
      { keys: 'Mod+\\', action: 'Toggle the sidebar' },
      { keys: 'Mod+.', action: 'Toggle the right panel' },
      { keys: 'Mod+/', action: 'This cheatsheet', note: 'Or just ?' },
      { keys: 'Mod+Shift+D', action: 'Toggle dark mode' },
      { keys: 'G then C', action: 'Go to the calendar' },
      { keys: 'G then A', action: 'Go to the agenda' },
      { keys: 'G then I', action: 'Go to the AI chat' },
      { keys: 'G then T', action: 'Go to the trash' },
      { keys: 'G then S', action: 'Go to settings' },
      { keys: 'Esc', action: 'Close the topmost overlay', note: 'One layer per press' },
    ],
  },
  {
    title: 'Editor',
    where: 'when the caret is in the document',
    shortcuts: [
      { keys: 'Mod+B', action: 'Bold' },
      { keys: 'Mod+I', action: 'Italic' },
      { keys: 'Mod+U', action: 'Underline' },
      { keys: 'Mod+Shift+X', action: 'Strikethrough' },
      {
        keys: 'Mod+Shift+C',
        action: 'Inline code',
        note: 'Not Mod+E — that is the AI panel',
      },
      { keys: 'Mod+Alt+1…3', action: 'Heading 1–3' },
      { keys: 'Mod+Alt+0', action: 'Paragraph' },
      { keys: 'Mod+Shift+7', action: 'Ordered list' },
      { keys: 'Mod+Shift+8', action: 'Bullet list' },
      { keys: 'Mod+Shift+9', action: 'Task list' },
      { keys: 'Mod+Shift+B', action: 'Blockquote' },
      { keys: 'Mod+Alt+C', action: 'Code block' },
      {
        keys: 'Mod+K',
        action: 'Insert a link',
        note: 'With a selection. Without one, it opens the palette',
      },
      { keys: 'Mod+F', action: 'Find in page' },
      { keys: 'Mod+Shift+H', action: 'Find and replace' },
      { keys: '[[', action: 'Wiki-link menu' },
      { keys: 'Tab / Shift+Tab', action: 'Indent / outdent a list item; next cell in a table' },
      { keys: 'Mod+Z / Mod+Shift+Z', action: 'Undo / redo' },
      { keys: 'Mod+Enter', action: 'Exit the current block', note: 'Escape a code block or table' },
    ],
  },
  {
    title: 'Tree',
    where: 'when a page row in the sidebar has focus',
    shortcuts: [
      { keys: '↑ / ↓', action: 'Move between rows' },
      {
        keys: '→ / ←',
        action: 'Expand / collapse',
        note: '← on a closed node jumps to its parent',
      },
      { keys: 'Enter', action: 'Open the page' },
      { keys: 'F2', action: 'Rename inline' },
      { keys: 'Mod+Backspace', action: 'Archive', note: 'Backspace and Delete do the same' },
    ],
  },
  {
    title: 'Chat',
    where: 'in the AI panel',
    shortcuts: [
      { keys: 'Enter', action: 'Send' },
      { keys: 'Shift+Enter', action: 'Newline' },
      { keys: 'Mod+Enter', action: 'Send', note: 'From anywhere in the panel' },
      { keys: 'Esc', action: 'Stop streaming, or close the panel' },
      { keys: 'Mod+Shift+A', action: 'Attach the current page as context' },
    ],
  },
  {
    title: 'Calendar',
    where: 'on a calendar screen',
    shortcuts: [
      { keys: 'T', action: 'Today' },
      { keys: 'W / M / A', action: 'Week / month / agenda view' },
      {
        keys: 'J / K',
        action: 'Next / previous period',
        note: 'On the agenda: next / previous row',
      },
      { keys: 'C', action: 'New event on the focused day', note: 'N does the same' },
      { keys: 'Space', action: 'Toggle done on the focused event' },
      { keys: 'Enter', action: 'Open the focused event' },
    ],
  },
] as const;

/* ── Rendering ──────────────────────────────────────────────────────────────── */

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform);

/**
 * `Mod+Shift+D` → `⌘ ⇧ D` on a Mac, `Ctrl Shift D` elsewhere. `G then C` keeps its word,
 * because a leader sequence is two presses and rendering it as one chord would be a lie.
 */
export function renderKeys(keys: string): string[] {
  return keys.split(' ').flatMap((chunk) => {
    if (chunk.toLowerCase() === 'then') return ['then'];
    if (chunk === '/') return ['/'];

    return [
      chunk
        .split('+')
        .map((part) => {
          if (part === 'Mod') return IS_MAC ? '⌘' : 'Ctrl';
          if (part === 'Shift') return IS_MAC ? '⇧' : 'Shift';
          if (part === 'Alt') return IS_MAC ? '⌥' : 'Alt';
          return part;
        })
        .join(IS_MAC ? ' ' : '+'),
    ];
  });
}
